import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Stack,
  Divider,
  Container,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { motion } from "framer-motion";
import TempleHinduIcon from "@mui/icons-material/TempleHindu";
import TerrainIcon from "@mui/icons-material/Terrain";
import KayakingIcon from "@mui/icons-material/Kayaking";
import LandscapeIcon from "@mui/icons-material/Landscape";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import SnowboardingIcon from "@mui/icons-material/Snowboarding";
import ForestIcon from "@mui/icons-material/Forest";
import PlaceIcon from "@mui/icons-material/Place";
import { useNavigate } from "react-router-dom";
import TourCard from "../Components/Cards/TourCard";

const UttarakhandPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const tourPackages = [
    {
      id: "uk1",
      title: "Complete Char Dham Yatra",
      places: "Haridwar • Yamunotri • Gangotri • Kedarnath • Badrinath • Rishikesh",
      price: "₹ 26,999/-",
      image:
        "https://uttarakhandtourism.gov.in/assets/media/UTDB_media_1740661833CHAR-DHAM.jpg",
      rating: 4.9,
    },
    {
      id: "uk2",
      title: "Rishikesh Adventure & Yoga Retreat",
      places: "Rishikesh • Shivpuri • Laxman Jhula • Neelkanth Mahadev",
      price: "₹ 10,499/-",
      image:
        "https://makemybharatyatra.com/uploads/packages/1727346890.jpg",
      rating: 4.7,
    },
    {
      id: "uk3",
      title: "Auli Snow Skiing & Joshimath Tour",
      places: "Joshimath • Auli • Gurso Bugyal • Tapovan",
      price: "₹ 15,499/-",
      image:
        "https://hblimg.mmtcdn.com/content/hubble/img/joshimathdestimgs/mmt/activities/t_ufs/m_Narsingh_Temple_1_l_479_640.jpg",
      rating: 4.8,
    },
    {
      id: "uk4",
      title: "Mussoorie, Nainital & Corbett",
      places: "Dehradun • Mussoorie • Nainital • Jim Corbett National Park",
      price: "₹ 12,999/-",
      image:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRLPAHYjmrXIxrFYveOYKDxuWw9Iud4DOJSKg&s",
      rating: 4.6,
    },
  ];

  const enrichTour = (tour, index) => {
    const durations = ["5D / 4N", "6D / 5N", "7D / 6N", "9D / 8N"];
    const tags = ["Group Tour", "Bestseller", "Limited Offer", "Popular"];

    return {
      ...tour,
      duration: durations[index % durations.length],
      tag: tags[index % tags.length],
      oldPrice:
        "₹ " +
        (parseInt(tour.price.replace(/[₹,/-]/g, "")) + 4000).toLocaleString() +
        "/-",
      discountEndsIn: "1d 3h 42m",
    };
  };

  return (
    <Box sx={{ backgroundColor: "#F9FAFB", minHeight: "100vh" }}>
      
      {/* HERO SECTION */}
      <Box
        sx={{
          position: "relative",
          height: { xs: "60vh", md: "75vh" },
          backgroundImage:
            "url(https://uttarakhandtourism.gov.in/assets/media/UTDB_media_1740661833CHAR-DHAM.jpg)", // Mountain landscape
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7))",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, textAlign: "center", color: "white" }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 800,
                fontSize: { xs: "2.5rem", md: "4.5rem" },
                mb: 2,
                textShadow: "0 4px 12px rgba(0,0,0,0.5)",
              }}
            >
              Uttarakhand
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 300, letterSpacing: 2, mb: 4, opacity: 0.9 }}
            >
              DEV BHOOMI (LAND OF GODS)
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => document.getElementById("packages").scrollIntoView({ behavior: "smooth" })}
              sx={{
                backgroundColor: "#D97706",
                color: "white",
                px: 4,
                py: 1.5,
                borderRadius: 50,
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: "0 4px 14px rgba(217, 119, 6, 0.4)",
                "&:hover": { backgroundColor: "#B45309", transform: "translateY(-2px)" },
              }}
            >
              Explore Packages
            </Button>
          </motion.div>
        </Container>
      </Box>

      {/* INTRO SECTION */}
      <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <Typography variant="overline" sx={{ color: "#059669", fontWeight: 700, letterSpacing: 1.5 }}>
            THE ABODE OF GODS
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mt: 1,
              mb: 3,
              color: "#1F2937"
            }}
          >
            Spirituality Meets Adventure
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#6B7280", lineHeight: 1.8, fontSize: "1.1rem" }}
          >
            Uttarakhand, famously known as <b>Devbhoomi</b>, is one of India’s most spiritually significant and naturally beautiful states. Nestled in the lap of the Himalayas, it offers a rare combination of sacred pilgrimage sites, adventure sports, snow-covered peaks, dense forests, pristine rivers, and charming hill towns. Whether you seek inner peace or thrilling experiences, Uttarakhand promises a journey that stays with you forever.
          </Typography>
        </motion.div>
      </Container>

      {/* PACKAGES SECTION */}
      <Box id="packages" sx={{ py: 6, backgroundColor: "#fff" }}>
        <Container maxWidth="xl">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 5,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Popular Tour Packages
          </Typography>
          
          <Grid container spacing={4}>
            {tourPackages.map((tour, index) => (
              <Grid item xs={12} sm={6} md={3} key={tour.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                >
                  <TourCard
                    tour={tour}
                    index={index}
                    onViewDetails={() =>
                      navigate("/tourdetails", {
                        state: enrichTour(tour, index),
                      })
                    }
                    onEnquire={() => navigate("/booking")}
                  />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* PLACES SECTION (ZIG-ZAG LAYOUT) */}
      <Box sx={{ py: 10, backgroundColor: "#F9FAFB" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 8,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Top Places to Visit
          </Typography>

          {[
            {
              title: "Rishikesh",
              img: "https://makemybharatyatra.com/uploads/packages/1727346890.jpg",
              desc: "Rishikesh is globally renowned as the Yoga Capital of the World. Located on the banks of the holy Ganga River, it attracts spiritual seekers, yoga practitioners, and adventure lovers alike. River rafting, Ganga Aarti, bungee jumping, and peaceful ashrams make it a must-visit destination.",
            },
            {
              title: "Kedarnath",
              img: "https://www.chardhamhotel.in/blog/wp-content/uploads/2024/09/Kedarnath-Temple.jpg",
              desc: "Kedarnath is one of the twelve Jyotirlingas of Lord Shiva and a key part of the Char Dham Yatra. Surrounded by snow-clad Himalayan peaks, the temple stands as a symbol of faith, devotion, and resilience.",
            },
            {
              title: "Auli",
              img: "https://triptradition.com/wp-content/uploads/2024/08/Uttarakhand-Auli-tour-package.jpg",
              desc: "Auli is India’s premier skiing destination, offering breathtaking views of Nanda Devi and other Himalayan peaks. During winter, Auli transforms into a snow paradise, ideal for skiing and snowboarding.",
            },
            {
              title: "Nainital",
              img: "https://uttarakhandtravelagency.com/images/Untitled%20design%20(1)_1752668538.jpg",
              desc: "Nainital is a charming hill station famous for its emerald-colored Naini Lake, colonial architecture, and cool climate. It is perfect for families, honeymooners, and leisure travelers.",
            },
          ].map((place, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: i % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Grid
                container
                spacing={6}
                alignItems="center"
                sx={{
                  mb: 10,
                  flexDirection: { xs: "column", md: i % 2 === 0 ? "row" : "row-reverse" },
                }}
              >
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      borderRadius: 4,
                      overflow: "hidden",
                      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                      height: { xs: "300px", md: "400px" },
                    }}
                  >
                    <CardMedia component="img" image={place.img} alt={place.title} sx={{ height: "100%", objectFit: "cover" }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="overline"
                    sx={{ color: "#059669", fontWeight: "bold" }}
                  >
                    DESTINATION
                  </Typography>
                  <Typography variant="h3" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, my: 2 }}>
                    {place.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#4B5563", lineHeight: 1.8 }}>
                    {place.desc}
                  </Typography>
                </Grid>
              </Grid>
            </motion.div>
          ))}
        </Container>
      </Box>

      {/* EXPERIENCES SECTION */}
      <Box sx={{ py: 10, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 2,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Experiences You Can Enjoy
          </Typography>
          <Typography sx={{ textAlign: "center", color: "#6B7280", mb: 6 }}>
            Discover the soul of Devbhoomi.
          </Typography>

          <Grid container spacing={3}>
            {[
              { icon: <TempleHinduIcon />, text: "Spiritual & Pilgrimage Tours" },
              { icon: <KayakingIcon />, text: "River Rafting & Water Sports" },
              { icon: <SnowboardingIcon />, text: "Skiing & Snow Adventures" },
              { icon: <DirectionsWalkIcon />, text: "Trekking & Hiking Trails" },
              { icon: <ForestIcon />, text: "Wildlife & Forest Safaris" },
              { icon: <LandscapeIcon />, text: "Scenic Valleys & Meadows" },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <motion.div
                  whileHover={{ y: -8 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: "100%",
                      borderRadius: 3,
                      border: "1px solid #E5E7EB",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      transition: "0.3s",
                      "&:hover": {
                        borderColor: "#059669",
                        boxShadow: "0 10px 30px -10px rgba(6, 78, 59, 0.15)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 40,
                        color: "#059669",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 60,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography fontWeight={600} sx={{ color: "#374151" }}>
                      {item.text}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* TABLE SECTION */}
      <Box sx={{ py: 10, backgroundColor: "#F3F4F6" }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box sx={{ p: 4, borderBottom: "1px solid #E5E7EB" }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#111827" }}>
                Major Tourist Destinations – Quick Overview
              </Typography>
            </Box>
            <Table>
              <TableHead sx={{ backgroundColor: "#064E3B" }}>
                <TableRow>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Place</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Altitude</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Region</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Famous For</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ["Rishikesh", "372 m", "Garhwal", "Yoga, Rafting, Ganga Aarti"],
                  ["Kedarnath", "3,583 m", "Garhwal", "Jyotirlinga Temple"],
                  ["Auli", "2,800 m", "Chamoli", "Skiing & Snowfall"],
                  ["Nainital", "1,938 m", "Kumaon", "Lakes & Hill Views"],
                  ["Mussoorie", "2,005 m", "Garhwal", "Hill Station"],
                  ["Jim Corbett", "600 m", "Ramnagar", "Wildlife Safari"],
                ].map((row, i) => (
                  <TableRow
                    key={i}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 }, "&:hover": { backgroundColor: "#F9FAFB" } }}
                  >
                    {row.map((cell, j) => (
                      <TableCell key={j} sx={{ color: "#4B5563" }}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Container>
      </Box>

      {/* CTA SECTION */}
      <Box
        sx={{
          py: 12,
          backgroundImage: "linear-gradient(135deg, #065F46 0%, #047857 100%)",
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: "2rem", md: "3rem" }
              }}
            >
              Plan Your Uttarakhand Journey Today
            </Typography>
            <Typography sx={{ maxWidth: "800px", mx: "auto", lineHeight: 1.8, fontSize: "1.1rem", opacity: 0.9, mb: 5 }}>
              From sacred temples to thrilling adventures and peaceful hill towns, Uttarakhand offers something for every traveler. Let us help you craft a memorable and comfortable journey with expert planning and trusted local experiences.
            </Typography>
            <Button
              size="large"
              variant="contained"
              sx={{
                backgroundColor: "#D97706",
                color: "white",
                fontWeight: 700,
                px: 6,
                py: 1.5,
                fontSize: "1.1rem",
                borderRadius: 50,
                boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
                "&:hover": { backgroundColor: "#B45309" },
              }}
              onClick={() => navigate("/booking")}
            >
              Enquire Now
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default UttarakhandPage;